<template>
  <div class="page-outer">
    <div class="page-header page-header-fixed-form">
      <div class="header-left">
        <h1 class="page-title">促銷規則</h1>
      </div>
    </div>

    <div class="page-container commerce-promo-page">
    <div class="commerce-rule-stack">
      <div class="settings-card">
        <div class="settings-card-header">
          <i class="ri-percent-line" style="color: #667eea;"></i>
          <div>
            <h2 class="settings-card-title">促銷規則</h2>
            <p class="settings-card-desc">
              與公開試算 API 相同資料表。促銷分<strong>主規則</strong>（整單僅一筆有折價）與<strong>加算</strong>（符合即可疊加）；皆依優先順序評估。
            </p>
          </div>
        </div>
        <div v-if="loadError" class="commerce-inline-alert alert alert-danger" role="alert">{{ loadError }}</div>
        <div class="promo-card-body">
          <div class="promo-table-scroll">
            <table class="promo-table">
              <thead>
                <tr>
                  <th scope="col">名稱</th>
                  <th scope="col" class="promo-table__col-app-mode">規則套用方式</th>
                  <th scope="col">使用方式</th>
                  <th scope="col">優惠／條件</th>
                  <th scope="col">有效期間</th>
                  <th scope="col" class="promo-table__col-num">優先順序</th>
                  <th scope="col" class="promo-table__col-flag">啟用</th>
                  <th scope="col" class="promo-table__col-actions">操作</th>
                </tr>
              </thead>
              <tbody>
                <template v-if="promoRules.length === 0">
                  <tr>
                    <td colspan="8" class="promo-table__empty">尚無促銷規則，請於下方新增。</td>
                  </tr>
                </template>
                <template v-else>
                  <tr
                    v-for="r in promoRules"
                    :key="r.id"
                    :class="{ 'promo-table__row--editing': editingPromoId === r.id }"
                  >
                    <td class="promo-table__cell-name">{{ r.name || '—' }}</td>
                    <td class="promo-table__col-app-mode">
                      <span
                        v-if="r.rule_type === 'commerce_cart_discount'"
                        class="promo-app-badge"
                        :class="
                          promoApplicationMode(r) === 'exclusive' ? 'promo-app-badge--exclusive' : 'promo-app-badge--stack'
                        "
                      >
                        {{ promoApplicationModeLabel(r) }}
                      </span>
                      <span v-else class="promo-table__cell-muted">—</span>
                    </td>
                    <td>{{ promoUsageSummary(r) }}</td>
                    <td class="promo-table__cell-detail">{{ promoConditionSummary(r) }}</td>
                    <td class="promo-table__cell-dates">{{ promoDateRange(r) }}</td>
                    <td class="promo-table__col-num">{{ r.priority }}</td>
                    <td class="promo-table__col-flag">
                      <span class="promo-badge" :class="r.is_active ? 'promo-badge--on' : 'promo-badge--off'">
                        {{ r.is_active ? '啟用' : '停用' }}
                      </span>
                    </td>
                    <td class="promo-table__col-actions">
                      <div class="promo-row-actions">
                        <button
                          v-if="r.rule_type === 'commerce_cart_discount'"
                          type="button"
                          class="btn-promo-edit"
                          @click="startEditPromo(r)"
                        >
                          編輯
                        </button>
                        <button type="button" class="btn-promo-delete" @click="openPromoDeleteLightbox(r)">刪除</button>
                      </div>
                    </td>
                  </tr>
                </template>
              </tbody>
            </table>
          </div>

          <section
            class="promo-add-panel"
            :class="{ 'promo-add-panel--edit': editingPromoId }"
            aria-labelledby="promo-form-title"
          >
            <h3 id="promo-form-title" class="promo-add-panel__title">
              {{ editingPromoId ? '編輯促銷' : '新增促銷' }}
            </h3>

            <div class="promo-form-layout">
            <div class="promo-form-grid">
              <div class="field">
                <label class="field-label field-label--required" for="promo-name">規則名稱</label>
                <input
                  id="promo-name"
                  v-model="promoForm.name"
                  class="form-control"
                  placeholder="例：春季滿額折"
                  required
                  maxlength="200"
                  autocomplete="off"
                />
              </div>
              <div class="field">
                <label class="field-label" for="promo-priority">優先順序</label>
                <input
                  id="promo-priority"
                  v-model.number="promoForm.priority"
                  type="number"
                  class="form-control"
                  placeholder="數字越小越先套用"
                />
              </div>
              <div class="field">
                <label class="field-label" for="promo-valid-from">生效日（起）</label>
                <input id="promo-valid-from" v-model="promoForm.validFrom" type="date" class="form-control" />
              </div>
              <div class="field">
                <label class="field-label" for="promo-valid-to">生效日（迄）</label>
                <input id="promo-valid-to" v-model="promoForm.validTo" type="date" class="form-control" />
              </div>

              <div class="field field--full">
                <span class="field-label" id="promo-app-mode-label">規則套用方式</span>
                <div class="radio-row" role="radiogroup" aria-labelledby="promo-app-mode-label">
                  <label class="radio-pill radio-pill--grow">
                    <input v-model="promoForm.applicationMode" type="radio" value="exclusive" />
                    主規則（整張訂單僅能有一筆主規則產生折價；多筆時依優先順序）
                  </label>
                  <label class="radio-pill radio-pill--grow">
                    <input v-model="promoForm.applicationMode" type="radio" value="stackable" />
                    加算（符合條件即可與其他加算規則依序疊加）
                  </label>
                </div>
                <p class="field-hint">
                  主規則與加算可並存。若已套用一筆主規則折價，其餘主規則將略過；加算規則仍會接續評估。
                </p>
              </div>

              <div class="field field--full">
                <label class="field-label" for="promo-usage">使用方式</label>
                <select id="promo-usage" v-model="promoForm.usageMode" class="form-control">
                  <option value="auto">自動計算（結帳試算直接套用）</option>
                  <option value="fixed_code">固定折扣代碼（試算／建單需傳 promoTriggerCodes）</option>
                  <option value="coupon_template">生成優惠券模板（僅儲存設定；發券與套用請搭配優惠券模組）</option>
                </select>
                <p v-if="promoForm.usageMode === 'fixed_code'" class="field-hint">
                  公開 API <code>POST /commerce/quote</code>、<code>POST /commerce/orders</code> 請帶入 <code>promoTriggerCodes: ["您的代碼"]</code>（不分大小寫）。
                </p>
              </div>

              <div v-if="promoForm.usageMode === 'fixed_code'" class="field">
                <label class="field-label" for="promo-code">折扣代碼</label>
                <input id="promo-code" v-model="promoForm.promoCode" class="form-control" placeholder="例：VIP88" autocomplete="off" />
              </div>

              <template v-if="promoForm.usageMode === 'coupon_template'">
                <div class="field field--grow">
                  <label class="field-label" for="promo-tpl">優惠券模板</label>
                  <select id="promo-tpl" v-model="promoForm.couponTemplateId" class="form-control">
                    <option value="">請選擇模板</option>
                    <option v-for="t in couponOptions" :key="t.id" :value="t.id">{{ t.templateName || t.id }}</option>
                  </select>
                </div>
                <div class="field">
                  <label class="field-label" for="promo-cap">發放／兌換數量上限</label>
                  <input
                    id="promo-cap"
                    v-model="promoForm.couponIssueMax"
                    type="number"
                    min="0"
                    class="form-control"
                    placeholder="選填，0 表示不寫入"
                  />
                </div>
              </template>

              <div class="field field--full">
                <label class="field-label">優惠方式</label>
                <div class="radio-row">
                  <label class="radio-pill">
                    <input v-model="promoForm.target" type="radio" value="order" />
                    對整筆訂單
                  </label>
                  <label class="radio-pill">
                    <input v-model="promoForm.target" type="radio" value="lines" />
                    對指定範圍商品（下列條件範圍內的小計為折扣基礎）
                  </label>
                </div>
              </div>

              <div class="field field--full">
                <label class="field-label" for="promo-scope">商品範圍</label>
                <select id="promo-scope" v-model="promoForm.lineFilterMode" class="form-control">
                  <option value="all">全館商品</option>
                  <option value="variants">指定規格（variant_id）</option>
                  <option value="category">指定分類（可含子分類）</option>
                </select>
              </div>

              <div v-if="promoForm.lineFilterMode === 'variants'" class="field field--full variant-pick-block">
                <label class="field-label">指定規格</label>
                <div class="variant-pick-actions">
                  <button type="button" class="btn btn-secondary btn-sm" @click="showVariantPicker = true">
                    <i class="ri-shopping-bag-3-line"></i>
                    選擇商品／規格
                  </button>
                  <span class="variant-pick-count">已選 {{ promoForm.selectedVariants.length }} 項</span>
                </div>
                <ul v-if="promoForm.selectedVariants.length" class="variant-chip-list">
                  <li v-for="(row, idx) in promoForm.selectedVariants" :key="row.variantId" class="variant-chip">
                    <span class="variant-chip-text">
                      <strong>{{ row.productName || '商品' }}</strong>
                      <template v-if="row.variantLabel"> · {{ row.variantLabel }}</template>
                      <span v-if="row.sku" class="variant-chip-sku">（{{ row.sku }}）</span>
                    </span>
                    <button
                      type="button"
                      class="variant-chip-remove"
                      :aria-label="'移除「' + (row.productName || '') + '」'"
                      @click="removeSelectedVariant(idx)"
                    >
                      <i class="ri-close-line"></i>
                    </button>
                  </li>
                </ul>
                <p v-else class="field-hint">
                  請點「選擇商品／規格」從清單加入（可多筆）；選完按選品視窗底部的「完成挑選」關閉。
                </p>
              </div>

              <div v-if="promoForm.lineFilterMode === 'category'" class="field field--grow">
                <label class="field-label" for="promo-cat">商品分類</label>
                <select id="promo-cat" v-model="promoForm.categoryId" class="form-control">
                  <option value="">請選擇</option>
                  <option v-for="c in categoriesFlat" :key="c.id" :value="c.id">{{ c.displayName }}</option>
                </select>
              </div>
              <div v-if="promoForm.lineFilterMode === 'category'" class="field field--checkbox-inline">
                <label class="field-checkbox">
                  <input v-model="promoForm.includeCategoryDescendants" type="checkbox" class="field-checkbox-input" />
                  <span>含子分類內所有商品</span>
                </label>
              </div>

              <div class="field">
                <label class="field-label" for="promo-min-qty">最少購買件數（符合範圍內累計）</label>
                <input
                  id="promo-min-qty"
                  v-model.number="promoForm.minQty"
                  type="number"
                  min="0"
                  class="form-control"
                  placeholder="0 表示不限制"
                />
              </div>

              <div class="field field--full">
                <label class="field-label">折扣方式</label>
                <div class="radio-row">
                  <label class="radio-pill">
                    <input v-model="promoForm.discountType" type="radio" value="fixed" />
                    固定金額
                  </label>
                  <label class="radio-pill">
                    <input v-model="promoForm.discountType" type="radio" value="percent" />
                    百分比
                  </label>
                </div>
              </div>
              <div v-if="promoForm.discountType === 'fixed'" class="field">
                <label class="field-label" for="promo-amt">折扣金額（元）</label>
                <input
                  id="promo-amt"
                  v-model.number="promoForm.discountAmount"
                  type="number"
                  min="0"
                  step="1"
                  class="form-control"
                />
              </div>
              <div v-else class="field">
                <label class="field-label" for="promo-pct">折扣百分比（%）</label>
                <input
                  id="promo-pct"
                  v-model.number="promoForm.discountPercent"
                  type="number"
                  min="0"
                  max="100"
                  step="0.1"
                  class="form-control"
                />
              </div>

              <div class="field">
                <label class="field-label" for="promo-min-spend">最低消費金額（元）</label>
                <input
                  id="promo-min-spend"
                  v-model="promoForm.minSpend"
                  type="number"
                  min="0"
                  step="1"
                  class="form-control"
                  placeholder="留空表示無最低消費（以整車商品小計判斷）"
                />
              </div>

              <div class="field field--checkbox">
                <label class="field-checkbox" for="promo-active">
                  <input id="promo-active" v-model="promoForm.is_active" type="checkbox" class="field-checkbox-input" />
                  <span>啟用此規則</span>
                </label>
              </div>
            </div>

            <aside class="promo-preview-panel" aria-label="設定效果示意試算">
              <div class="promo-preview-panel__head">
                <i class="ri-lightbulb-line" aria-hidden="true"></i>
                <div>
                  <h4 class="promo-preview-panel__title">{{ promoPreview.headline }}</h4>
                  <p class="promo-preview-panel__sub">
                    以下為<strong>固定示意數字</strong>（整車 3,000 元、兩列商品），用來對照您目前的條件；實際結帳仍依
                    <code>POST /commerce/quote</code> 與規則優先順序為準。
                  </p>
                </div>
              </div>

              <template v-if="promoPreview.kind === 'coupon_template'">
                <ul class="promo-preview-list">
                  <li v-for="(line, i) in promoPreview.bodyLines" :key="i">{{ line }}</li>
                </ul>
              </template>

              <template v-else>
                <p v-if="promoPreview.blocked" class="promo-preview-blocked" role="status">{{ promoPreview.blocked }}</p>

                <dl v-else class="promo-preview-dl">
                  <div class="promo-preview-dl__row">
                    <dt>套用方式</dt>
                    <dd>{{ promoPreview.applicationModeLabel }}</dd>
                  </div>
                  <div class="promo-preview-dl__row">
                    <dt>商品範圍</dt>
                    <dd>{{ promoPreview.scopeLabel }}</dd>
                  </div>
                  <div class="promo-preview-dl__row">
                    <dt>優惠標的</dt>
                    <dd>{{ promoPreview.targetLabel }}</dd>
                  </div>
                  <div class="promo-preview-dl__row">
                    <dt>示意購物車</dt>
                    <dd>
                      <ul class="promo-preview-mini-lines">
                        <li v-for="(row, i) in promoPreview.demoLines" :key="i">
                          {{ row.label }} ×{{ row.qty }}，小計 {{ fmtPreview(row.lineTotal) }}
                        </li>
                      </ul>
                      <span class="promo-preview-total">整車小計 {{ fmtPreview(promoPreview.demoCartTotal) }}</span>
                    </dd>
                  </div>
                  <div class="promo-preview-dl__row">
                    <dt>符合範圍列</dt>
                    <dd>
                      件數合計 <strong>{{ promoPreview.eligibleQty }}</strong>，小計
                      <strong>{{ fmtPreview(promoPreview.eligibleSubtotal) }}</strong>
                    </dd>
                  </div>
                  <div class="promo-preview-dl__row">
                    <dt>折價計算基礎</dt>
                    <dd>{{ fmtPreview(promoPreview.discountBase) }}</dd>
                  </div>
                  <div class="promo-preview-dl__row">
                    <dt>折價公式</dt>
                    <dd>
                      <template v-if="promoPreview.discountType === 'percent'">
                        {{ fmtPreview(promoPreview.discountBase) }} × {{ promoPreview.discountPercent || 0 }}% ≈
                        {{ fmtPreview(promoPreview.rawCalc) }}
                      </template>
                      <template v-else> 固定折 {{ fmtPreview(promoPreview.discountAmount || 0) }} </template>
                      <span class="promo-preview-cap">（上限不超過整車小計 → {{ fmtPreview(promoPreview.capped) }}）</span>
                    </dd>
                  </div>
                  <div class="promo-preview-dl__row promo-preview-dl__row--highlight">
                    <dt>本條規則折價</dt>
                    <dd>{{ fmtPreview(promoPreview.discount) }}</dd>
                  </div>
                </dl>

                <ul v-if="promoPreview.checks.length" class="promo-preview-notes">
                  <li v-for="(c, i) in promoPreview.checks" :key="i">{{ c }}</li>
                </ul>
              </template>
            </aside>
            </div>

            <div class="promo-form-actions">
              <button v-if="editingPromoId" type="button" class="btn btn-secondary" :disabled="saving" @click="cancelEditPromo">
                取消編輯
              </button>
              <button type="button" class="btn btn-primary" :disabled="saving" @click="savePromo">
                {{ saving ? '處理中…' : editingPromoId ? '儲存變更' : '新增規則' }}
              </button>
            </div>
          </section>
        </div>
      </div>
    </div>

    <ProductPickerModal
      :open="showVariantPicker"
      title="選擇適用規格（可多筆）"
      :variantRequired="true"
      :stayOpenOnSelect="true"
      :excludeProductTypes="['bundle']"
      @close="showVariantPicker = false"
      @select="onPromoVariantPick"
    />

    <Teleport to="body">
      <div
        v-if="promoDeleteLightboxOpen"
        class="promo-delete-overlay"
        role="dialog"
        aria-modal="true"
        aria-labelledby="promo-delete-title"
        @click.self="closePromoDeleteLightbox"
      >
        <div class="promo-delete-dialog">
          <h3 id="promo-delete-title" class="promo-delete-title">確定刪除促銷規則？</h3>
          <p class="promo-delete-text">
            將刪除「<strong>{{ promoDeleteDisplayName }}</strong>」，此操作無法復原。
          </p>
          <div class="promo-delete-actions">
            <button
              type="button"
              class="btn btn-secondary"
              :disabled="promoDeleteSubmitting"
              @click="closePromoDeleteLightbox"
            >
              取消
            </button>
            <button type="button" class="btn btn-danger" :disabled="promoDeleteSubmitting" @click="confirmPromoDelete">
              {{ promoDeleteSubmitting ? '刪除中…' : '刪除' }}
            </button>
          </div>
        </div>
      </div>
    </Teleport>
    </div>
  </div>
</template>

<script setup>
import { computed, onMounted, ref, watch } from 'vue'
import { usePageTitle } from '@/composables/usePageTitle.js'
import { useCommerceAdvancedRules } from '@/composables/useCommerceAdvancedRules.js'
import { productService } from '@/api/modules/products.js'
import { couponService } from '@/api/modules/coupons.js'
import ProductPickerModal from '@/components/ProductPickerModal.vue'
import { showWarning } from '@/utils/swal.js'
import { computePromoFormPreview } from '@/utils/commercePromoPreview.js'

usePageTitle('促銷規則')

const categoriesFlat = ref([])
const couponOptions = ref([])
const showVariantPicker = ref(false)

const promoDeleteLightboxOpen = ref(false)
const promoDeleteTargetId = ref(null)
const promoDeleteDisplayName = ref('')
const promoDeleteSubmitting = ref(false)

const {
  loadError,
  saving,
  promoRules,
  promoForm,
  editingPromoId,
  reload,
  savePromo,
  startEditPromo,
  cancelEditPromo,
  removePromo,
} = useCommerceAdvancedRules()

function openPromoDeleteLightbox(rule) {
  promoDeleteTargetId.value = rule.id
  promoDeleteDisplayName.value = (rule.name && String(rule.name).trim()) || '（未命名）'
  promoDeleteLightboxOpen.value = true
}

function resetPromoDeleteLightbox() {
  promoDeleteLightboxOpen.value = false
  promoDeleteTargetId.value = null
  promoDeleteDisplayName.value = ''
}

function closePromoDeleteLightbox() {
  if (promoDeleteSubmitting.value) return
  resetPromoDeleteLightbox()
}

async function confirmPromoDelete() {
  const id = promoDeleteTargetId.value
  if (!id) return
  promoDeleteSubmitting.value = true
  loadError.value = ''
  try {
    await removePromo(id)
    resetPromoDeleteLightbox()
  } catch (e) {
    loadError.value = e?.response?.data?.message || e.message || '刪除失敗'
  } finally {
    promoDeleteSubmitting.value = false
  }
}

const promoPreview = computed(() => computePromoFormPreview(promoForm.value))

function fmtPreview(n) {
  const x = Number(n)
  if (!Number.isFinite(x)) return '—'
  return `NT$ ${Math.round(x).toLocaleString()}`
}

watch(
  () => promoForm.value.lineFilterMode,
  (mode, prev) => {
    if (prev === 'variants' && mode !== 'variants') {
      promoForm.value.selectedVariants = []
    }
  }
)

function formatVariantPickLabel(variant) {
  if (variant?.attributes && Object.keys(variant.attributes).length > 0) {
    return Object.values(variant.attributes).filter(Boolean).join(' / ')
  }
  return variant?.sku || '規格'
}

function onPromoVariantPick({ product, variant }) {
  if (!variant?.id) return
  const id = String(variant.id)
  if (!Array.isArray(promoForm.value.selectedVariants)) {
    promoForm.value.selectedVariants = []
  }
  const list = promoForm.value.selectedVariants
  if (list.some((v) => v.variantId === id)) {
    void showWarning('已選過', '此規格已在清單中。')
    return
  }
  list.push({
    variantId: id,
    productName: product?.name || '',
    variantLabel: formatVariantPickLabel(variant),
    sku: variant.sku || '',
  })
}

function removeSelectedVariant(index) {
  promoForm.value.selectedVariants?.splice(index, 1)
}

function parseCfg(config) {
  if (config == null) return {}
  if (typeof config === 'object') return config
  if (typeof config === 'string') {
    try {
      return JSON.parse(config)
    } catch {
      return {}
    }
  }
  return {}
}

/** @returns {'exclusive'|'stackable'} */
function promoApplicationMode(rule) {
  if (rule.rule_type !== 'commerce_cart_discount') return 'stackable'
  const c = parseCfg(rule.config)
  const am = c.applicationMode ?? c.application_mode
  return String(am).toLowerCase() === 'exclusive' ? 'exclusive' : 'stackable'
}

function promoApplicationModeLabel(rule) {
  return promoApplicationMode(rule) === 'exclusive' ? '主規則' : '加算'
}

function promoUsageSummary(rule) {
  if (rule.rule_type !== 'commerce_cart_discount') return rule.rule_type
  const c = parseCfg(rule.config)
  const m = c.usageMode || c.usage_mode || 'auto'
  if (m === 'fixed_code') return `固定代碼：${c.promoCode || c.promo_code || '—'}`
  if (m === 'coupon_template') {
    const cap = c.couponIssueMax ?? c.coupon_issue_max
    return cap != null && cap !== '' ? `優惠券模板（上限 ${cap}）` : '優惠券模板'
  }
  return '自動計算'
}

function promoConditionSummary(rule) {
  if (rule.rule_type !== 'commerce_cart_discount') return '—'
  const c = parseCfg(rule.config)
  const lf = c.lineFilter || {}
  const parts = []
  if (c.target === 'lines') parts.push('對指定商品')
  else parts.push('對訂單')
  if (lf.mode === 'variants') parts.push('指定規格')
  else if (lf.mode === 'category') parts.push(lf.includeCategoryDescendants ? '指定分類含子類' : '指定分類')
  else parts.push('全館')
  const mq = c.minQty ?? c.min_qty
  if (mq > 0) parts.push(`至少 ${mq} 件`)
  const ms = c.minSpend ?? c.min_spend
  if (ms != null && Number(ms) > 0) parts.push(`滿 ${ms} 元`)
  const dt = c.discountType || c.discount_type
  if (dt === 'percent') parts.push(`折 ${c.discountPercent ?? c.discount_percent ?? 0}%`)
  else parts.push(`折 ${c.discountAmount ?? c.discount_amount ?? 0} 元`)
  return parts.join(' · ')
}

function promoDateRange(rule) {
  const a = rule.valid_from ?? rule.validFrom
  const b = rule.valid_to ?? rule.validTo
  if (!a && !b) return '不限'
  const fmt = (x) => (x ? String(x).slice(0, 10) : '—')
  return `${fmt(a)} ~ ${fmt(b)}`
}

async function loadCategories() {
  try {
    const res = await productService.getCategoriesFlat()
    const body = res?.data ?? res
    const raw = body?.data ?? body
    const arr = Array.isArray(raw) ? raw : []
    categoriesFlat.value = arr.map((c) => ({
      ...c,
      displayName: c.parentName ? `${c.parentName} / ${c.name}` : c.name,
    }))
  } catch {
    categoriesFlat.value = []
  }
}

async function loadCouponTemplates() {
  try {
    const res = await couponService.getTemplatesDropdown({ isActive: true, pageSize: 200 })
    const body = res?.data ?? res
    const raw = body?.data ?? body
    couponOptions.value = Array.isArray(raw) ? raw : []
  } catch {
    couponOptions.value = []
  }
}

onMounted(async () => {
  await Promise.all([reload(), loadCategories(), loadCouponTemplates()])
})
</script>

<style scoped>
@import '~@/assets/scss/components/_list-page-common.scss';

.commerce-rule-stack {
  width: 100%;
  max-width: 100%;
}

.settings-card {
  background: #fff;
  border: 1px solid #e9ecef;
  border-radius: 12px;
  overflow: hidden;
}

.settings-card-header {
  display: flex;
  align-items: flex-start;
  gap: 16px;
  padding: 24px;
  background: #f8f9ff;
  border-bottom: 1px solid #e9ecef;
}

.settings-card-header > i {
  font-size: 32px;
  margin-top: 2px;
  flex-shrink: 0;
}

.settings-card-title {
  font-size: 18px;
  font-weight: 600;
  margin: 0 0 4px;
  color: #2d3748;
}

.settings-card-desc {
  font-size: 13px;
  color: #6c757d;
  margin: 0;
  line-height: 1.5;
}

.settings-card-desc code {
  font-size: 12px;
  background: #eef2ff;
  padding: 1px 6px;
  border-radius: 4px;
}

.commerce-inline-alert {
  margin: 16px 24px;
}

.alert {
  padding: 10px 14px;
  border-radius: 8px;
  font-size: 13px;
}

.alert-danger {
  background: #fff5f5;
  color: #c53030;
  border: 1px solid #fed7d7;
}

.promo-card-body {
  padding: 20px 24px 24px;
}

.promo-table-scroll {
  overflow-x: auto;
  margin-bottom: 24px;
  border: 1px solid #e9ecef;
  border-radius: 8px;
}

.promo-table {
  width: 100%;
  min-width: 980px;
  border-collapse: collapse;
  font-size: 13px;
}

.promo-table th,
.promo-table td {
  padding: 12px 14px;
  text-align: left;
  vertical-align: middle;
  border-bottom: 1px solid #eef0f3;
}

.promo-table thead th {
  background: #eef1f5;
  font-size: 13px;
  font-weight: 600;
  color: #1e293b;
  text-transform: none;
  letter-spacing: normal;
  border-bottom: 1px solid #dee2e6;
}

.promo-table tbody tr:hover {
  background: #fafbff;
}

.promo-table__col-app-mode {
  white-space: nowrap;
  width: 1%;
  vertical-align: middle;
}

.promo-table__cell-muted {
  color: #9ca3af;
}

.promo-app-badge {
  display: inline-block;
  padding: 3px 10px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 600;
}

.promo-app-badge--exclusive {
  background: #ede9fe;
  color: #5b21b6;
  border: 1px solid #ddd6fe;
}

.promo-app-badge--stack {
  background: #ecfdf5;
  color: #047857;
  border: 1px solid #a7f3d0;
}

.promo-table__col-num {
  text-align: right;
  white-space: nowrap;
  width: 1%;
}

.promo-table__col-flag {
  text-align: center;
  white-space: nowrap;
  width: 1%;
}

.promo-table__col-actions {
  text-align: right;
  white-space: nowrap;
  width: 1%;
}

.promo-table__cell-name {
  font-weight: 500;
  color: #2d3748;
}

.promo-table__cell-detail,
.promo-table__cell-dates {
  color: #4b5563;
  max-width: 280px;
  word-break: break-word;
}

.promo-table__empty {
  text-align: center;
  color: #9ca3af;
  padding: 28px 16px !important;
}

.promo-table__row--editing td {
  background: #eef2ff;
}

.promo-badge {
  display: inline-block;
  padding: 2px 10px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 500;
}

.promo-badge--on {
  background: #d1fae5;
  color: #047857;
}

.promo-badge--off {
  background: #f3f4f6;
  color: #6b7280;
}

.promo-row-actions {
  display: inline-flex;
  flex-wrap: wrap;
  gap: 6px;
  justify-content: flex-end;
}

.btn-promo-edit {
  padding: 4px 10px;
  font-size: 12px;
  font-weight: 500;
  color: #4338ca;
  background: #fff;
  border: 1px solid #c7d2fe;
  border-radius: 6px;
  cursor: pointer;
}

.btn-promo-delete {
  padding: 4px 10px;
  font-size: 12px;
  font-weight: 500;
  color: #b91c1c;
  background: #fff;
  border: 1px solid #fecaca;
  border-radius: 6px;
  cursor: pointer;
}

.promo-add-panel {
  margin-top: 8px;
  padding: 20px;
  background: linear-gradient(180deg, #fafbff 0%, #f8f9fa 100%);
  border: 1px solid #e9ecef;
  border-radius: 10px;
}

.promo-add-panel--edit {
  border-color: #c7d2fe;
  box-shadow: 0 0 0 1px rgba(102, 126, 234, 0.12);
}

.promo-add-panel__title {
  margin: 0 0 16px;
  font-size: 15px;
  font-weight: 600;
  color: #2d3748;
}

.promo-form-layout {
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(288px, 360px);
  gap: 24px 28px;
  align-items: start;
  margin-bottom: 16px;
}

@media (max-width: 1024px) {
  .promo-form-layout {
    grid-template-columns: 1fr;
  }

  .promo-preview-panel {
    position: static;
  }
}

.promo-form-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(240px, 1fr));
  gap: 14px 18px;
  align-items: start;
  margin-bottom: 0;
}

.promo-preview-panel {
  position: sticky;
  top: 12px;
  padding: 16px 18px;
  background: #fff;
  border: 1px solid #e0e7ff;
  border-radius: 10px;
  box-shadow: 0 2px 10px rgba(102, 126, 234, 0.08);
}

.promo-preview-panel__head {
  display: flex;
  gap: 12px;
  margin-bottom: 14px;
}

.promo-preview-panel__head > i {
  font-size: 22px;
  color: #667eea;
  flex-shrink: 0;
  margin-top: 2px;
}

.promo-preview-panel__title {
  margin: 0 0 6px;
  font-size: 14px;
  font-weight: 600;
  color: #1e293b;
}

.promo-preview-panel__sub {
  margin: 0;
  font-size: 12px;
  color: #64748b;
  line-height: 1.5;
}

.promo-preview-panel__sub code {
  font-size: 11px;
  background: #f1f5f9;
  padding: 1px 5px;
  border-radius: 4px;
}

.promo-preview-list {
  margin: 0;
  padding-left: 1.1rem;
  font-size: 13px;
  color: #475569;
  line-height: 1.55;
}

.promo-preview-blocked {
  margin: 0 0 12px;
  padding: 10px 12px;
  font-size: 13px;
  color: #9a3412;
  background: #fff7ed;
  border: 1px solid #fed7aa;
  border-radius: 8px;
  line-height: 1.45;
}

.promo-preview-dl {
  margin: 0;
}

.promo-preview-dl__row {
  display: grid;
  grid-template-columns: 108px 1fr;
  gap: 8px 12px;
  padding: 8px 0;
  border-bottom: 1px solid #f1f5f9;
  font-size: 13px;
}

.promo-preview-dl__row:last-child {
  border-bottom: none;
}

.promo-preview-dl__row dt {
  margin: 0;
  color: #64748b;
  font-weight: 500;
}

.promo-preview-dl__row dd {
  margin: 0;
  color: #1e293b;
  line-height: 1.45;
}

.promo-preview-dl__row--highlight {
  padding-top: 10px;
  margin-top: 4px;
  border-top: 1px solid #e0e7ff;
  border-bottom: none;
}

.promo-preview-dl__row--highlight dt,
.promo-preview-dl__row--highlight dd {
  font-weight: 600;
  color: #4338ca;
}

.promo-preview-mini-lines {
  margin: 0 0 8px;
  padding-left: 1.1rem;
  color: #475569;
}

.promo-preview-total {
  font-size: 12px;
  color: #64748b;
}

.promo-preview-cap {
  display: block;
  margin-top: 4px;
  font-size: 12px;
  color: #64748b;
}

.promo-preview-notes {
  margin: 12px 0 0;
  padding: 10px 12px 10px 1.25rem;
  font-size: 12px;
  color: #475569;
  background: #f8fafc;
  border-radius: 8px;
  line-height: 1.5;
}

.field {
  display: flex;
  flex-direction: column;
  gap: 6px;
  min-width: 0;
}

.field-label {
  font-size: 12px;
  color: #4b5563;
  font-weight: 600;
}

.field-label--required::after {
  content: ' *';
  color: #b91c1c;
  font-weight: 700;
}

.field--full {
  grid-column: 1 / -1;
}

.field--grow {
  min-width: 220px;
}

.field--checkbox {
  grid-column: 1 / -1;
}

.field--checkbox-inline {
  display: flex;
  align-items: flex-end;
  padding-bottom: 4px;
}

.field-hint {
  margin: 4px 0 0;
  font-size: 12px;
  color: #6b7280;
  line-height: 1.45;
}

.field-hint code {
  font-size: 11px;
  background: #fff;
  padding: 0 4px;
  border-radius: 4px;
}

.radio-row {
  display: flex;
  flex-wrap: wrap;
  gap: 10px 16px;
  align-items: center;
}

.radio-pill {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  color: #374151;
  cursor: pointer;
}

.radio-pill--grow {
  flex: 1 1 200px;
}

.min-spend-input {
  max-width: 140px;
}

.variant-pick-block {
  margin-top: 4px;
}

.variant-pick-actions {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 12px;
  margin-bottom: 10px;
}

.variant-pick-count {
  font-size: 13px;
  color: #6b7280;
}

.variant-chip-list {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.variant-chip {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  padding: 10px 12px;
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  font-size: 13px;
}

.variant-chip-text {
  min-width: 0;
  color: #374151;
}

.variant-chip-sku {
  color: #6b7280;
  font-weight: 400;
}

.variant-chip-remove {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border: none;
  border-radius: 6px;
  background: #fef2f2;
  color: #b91c1c;
  cursor: pointer;
  transition: background 0.15s;
}

.variant-chip-remove:hover {
  background: #fee2e2;
}

.field-checkbox {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  font-size: 13px;
  color: #374151;
  font-weight: 500;
  cursor: pointer;
}

.field-checkbox-input {
  margin-top: 2px;
  width: 16px;
  height: 16px;
  accent-color: #667eea;
  flex-shrink: 0;
}

.promo-form-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  justify-content: flex-end;
  padding-top: 12px;
  border-top: 1px solid #e9ecef;
}

.promo-delete-overlay {
  position: fixed;
  inset: 0;
  z-index: 1100;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
  background: rgba(15, 23, 42, 0.45);
}

.promo-delete-dialog {
  width: 100%;
  max-width: 400px;
  padding: 22px 24px 20px;
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 16px 48px rgba(0, 0, 0, 0.2);
}

.promo-delete-title {
  margin: 0 0 12px;
  font-size: 17px;
  font-weight: 600;
  color: #1e293b;
}

.promo-delete-text {
  margin: 0 0 20px;
  font-size: 14px;
  color: #475569;
  line-height: 1.55;
}

.promo-delete-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  justify-content: flex-end;
}
</style>
